import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { IdCard, RotateCw } from "lucide-react";
import { toast } from "sonner";

const errText = (e) => formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong";
const IMPORT_URL = "/hr/app/_erp/import-users";   // under /hr/app/ so the admin's HR session goes along

const MATCH_CLS = {
  "same e-mail": "bg-green-50 text-green-700",
  "same name": "bg-green-50 text-green-700",
  alias: "bg-blue-50 text-blue-700",
  "similar spelling": "bg-amber-50 text-amber-700",
};

/* Users & Roles → Import from HR app (backend/hr_user_import.py). Active HR
   app employees become ERP users. Anyone already in the ERP — same e-mail,
   name, alias (Priyanka = Priyanka C H) or similar spelling — keeps their own
   ERP login and is only linked to their HR record; the rest get a new login
   — their HR employee code, with their HR app password (checked by the HR
   app at sign-in; the ERP keeps no password for them) — Dashboard +
   HR only. Every row's choice can be changed before saving. */
export default function HrUserImport({ onDone }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [choices, setChoices] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true); setError(""); setPreview(null);
    try {
      const { data } = await api.get(IMPORT_URL);
      setPreview(data);
      setChoices(Object.fromEntries(data.rows.filter((r) => r.action !== "linked")
        .map((r) => [r.emp_code, r.action === "link" ? r.erp_user_id : r.action === "create" ? "new" : "skip"])));
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };
  const start = () => { setOpen(true); load(); };

  const rows = useMemo(() => preview?.rows || [], [preview]);
  const open_ = rows.filter((r) => r.action !== "linked");
  const done = rows.filter((r) => r.action === "linked");
  const picks = Object.values(choices);
  const nNew = picks.filter((p) => p === "new").length;
  const nLink = picks.filter((p) => p !== "new" && p !== "skip").length;
  const dupes = new Set(picks.filter((p, i) => p !== "new" && p !== "skip" && picks.indexOf(p) !== i));
  const userOf = (id) => preview?.erp_users.find((u) => u.id === id);

  const run = async () => {
    if (dupes.size) return toast.error("One ERP user is chosen for two employees — fix the highlighted rows");
    if (!window.confirm(`Create ${nNew} new user(s) and link ${nLink} existing ERP user(s) to their HR records?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(IMPORT_URL, { choices });
      toast.success(`${data.created.length} new user(s) added, ${data.linked.length} existing user(s) linked`);
      setOpen(false);
      onDone();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={start} data-testid="hr-import-users-btn">
        <IdCard className="w-4 h-4 mr-1.5" /> Import from HR app
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="hr-import-users">
          <DialogHeader>
            <DialogTitle>Import employees from the HR app</DialogTitle>
            <DialogDescription>
              Employees already in the ERP — same e-mail, same name, an alias (Priyanka = Priyanka C H, Mr. Mahesh = Mahesh V H)
              or a similar spelling — keep their own ERP login and are only linked to their HR record. The others sign in
              with their HR employee code and HR app password, with the role Operator and only Dashboard and HR.
              Check the ERP account column and change any row before saving.
            </DialogDescription>
          </DialogHeader>

          {busy && !preview && <div className="text-sm text-slate-500">Reading the HR app's employees…</div>}
          {error && (
            <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2 space-y-1">
              <div>{error}</div>
              <div className="flex gap-2 items-center">
                <Link to="/hr" className="text-blue-600 hover:underline" onClick={() => setOpen(false)}>Open HR → HR App</Link>
                <span className="text-slate-400">·</span>
                <button type="button" className="text-blue-600 hover:underline inline-flex items-center gap-1" onClick={load}><RotateCw className="w-3 h-3" />Try again</button>
              </div>
            </div>
          )}

          {preview && (
            <div className="space-y-3">
              <div className="text-sm">
                <b>{nNew}</b> new user(s) · <b>{nLink}</b> linked to existing ERP users
                <span className="text-slate-500"> · {picks.length - nNew - nLink} skipped · {done.length} already linked earlier</span>
              </div>
              {open_.length > 0 && (
                <div className="rounded-md border border-slate-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                      <tr><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">HR name</th><th className="text-left px-3 py-2">Department</th><th className="text-left px-3 py-2">ERP account</th><th className="text-left px-3 py-2">Match</th></tr>
                    </thead>
                    <tbody>
                      {open_.map((r) => {
                        const pick = choices[r.emp_code];
                        const isLink = pick !== "new" && pick !== "skip";
                        const suggested = r.action === "link" && pick === r.erp_user_id;
                        return (
                          <tr key={r.emp_code} className={`border-t border-slate-100 ${dupes.has(pick) ? "bg-red-50" : ""}`} data-testid={`hr-import-row-${r.emp_code}`}>
                            <td className="px-3 py-1.5 font-mono text-xs">{r.emp_code}</td>
                            <td className="px-3 py-1.5">
                              {r.name}
                              {r.hr_login === false && <span className="ml-1.5 text-[10px] rounded bg-amber-50 text-amber-700 px-1 py-0.5" title="This employee has no HR app login yet, so they can't sign in until HR creates one">no HR login yet</span>}
                            </td>
                            <td className="px-3 py-1.5 text-slate-600">{r.department || "—"}</td>
                            <td className="px-3 py-1.5">
                              <select
                                className="w-full min-w-[260px] h-8 border border-slate-200 rounded-md px-2 text-xs bg-white"
                                value={pick}
                                onChange={(e) => setChoices({ ...choices, [r.emp_code]: e.target.value })}
                                data-testid={`hr-import-choice-${r.emp_code}`}
                              >
                                {r.new_login && <option value="new">New login: {r.new_login} (HR password)</option>}
                                <option value="skip">Skip — don't add</option>
                                <optgroup label="Existing ERP user (keeps their login)">
                                  {preview.erp_users.map((u) => (
                                    <option key={u.id} value={u.id} disabled={!!u.hr_emp_code && u.hr_emp_code !== r.emp_code}>
                                      {u.name} — {u.email}{u.hr_emp_code ? ` (HR ${u.hr_emp_code})` : ""}
                                    </option>
                                  ))}
                                </optgroup>
                              </select>
                            </td>
                            <td className="px-3 py-1.5 text-xs whitespace-nowrap">
                              {isLink && suggested && <span className={`rounded px-1.5 py-0.5 ${MATCH_CLS[r.match] || "bg-slate-100 text-slate-600"}`}>{r.match}</span>}
                              {isLink && !suggested && <span className="text-slate-500">chosen · {userOf(pick)?.email}</span>}
                              {pick === "skip" && r.action === "skip" && <span className="text-slate-500">{r.match}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {done.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-slate-600">Already linked earlier ({done.length})</summary>
                  <ul className="mt-2 space-y-0.5 text-xs text-slate-500">
                    {done.map((r) => <li key={r.emp_code}><span className="font-mono">{r.emp_code}</span> {r.name} → {r.erp_user?.name} ({r.erp_user?.email})</li>)}
                  </ul>
                </details>
              )}
              {nNew > 0 && (
                <p className="text-xs text-slate-600 rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
                  New users sign in to the ERP with their <b>employee code</b> (e.g. {open_.find((r) => choices[r.emp_code] === "new")?.emp_code || "CAPL_1052"})
                  and the <b>password they use in the HR app</b> — the ERP checks it with the HR app, so no ERP password is set.
                  Someone with no HR login yet (marked below) can sign in once HR creates one for them.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            {preview && nNew + nLink > 0 && (
              <Button onClick={run} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="hr-import-run">
                Add {nNew} · link {nLink}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
